import { AdvancedApiError } from '../api/client'
import { joinName } from '../format'
import { AdvancedAdminPatientFragment } from './adminCareTeams.generated'

export type AdminPatient = AdvancedAdminPatientFragment

export type Failure = 'standardRead' | 'noAdminClaim' | 'serviceOutdated' | 'serviceDown' | 'refused'

export type AddResult = { result: 'added' | 'not_on_standard' } | { result: 'failed'; failure: Failure }

export type Outcome = AddResult & { patient: AdminPatient }

export type CoachProfileShape = { licenseType?: string; regions?: Record<string, { general?: boolean }> }

const ADVANCED_FAILURES: Record<number, Failure> = { 401: 'noAdminClaim', 404: 'serviceOutdated' }

// A 404 on an admin write means the route is missing: those routes take no care-team gate.
export const advancedFailure = (error: unknown): Failure =>
  !(error instanceof AdvancedApiError) || error.status >= 500
    ? 'serviceDown'
    : ADVANCED_FAILURES[error.status] ?? 'refused'

// Mirrors server's isPermanentCareteamMember.
const KEPT_LICENSE_TYPES = ['MD', 'RD']

export const patientName = (patient: AdminPatient) => joinName(patient.firstname, patient.lastname) || patient.uid

export const isKeptByStandard = (profile: CoachProfileShape | undefined) =>
  profile?.regions?.NUTRITION_COACHING?.general === true || KEPT_LICENSE_TYPES.includes(profile?.licenseType ?? '')

export const isOnStandardCareteam = (patient: AdminPatient, coachId: string) =>
  (patient.careTeam ?? []).some((member) => member.coachID === coachId)

export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  run: (item: T) => Promise<R>,
  onSettled: () => void = () => undefined
): Promise<R[]> => {
  const results: R[] = []
  let next = 0

  const worker = async (): Promise<void> => {
    const index = next
    next += 1

    if (index >= items.length) {
      return
    }

    results[index] = await run(items[index])
    onSettled()
    await worker()
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))

  return results
}
